import {useContext, useState, useEffect} from "react"
import {useParams} from "react-router-dom"
import useSWR from "swr"

import OSRHeader from "../../components/surveys/osr/OSRHeader"
import {wrappedPatch} from "../../utilities/AxiosHelper"
import QuestionRenderer from "../../components/prompts/QuestionRenderer"
import SurveyComplete from "../../components/surveys/SurveyComplete"
import SurveyProgressBar from "../../components/surveys/SurveyProgressBar"
import SurveyNavigation from "../../components/surveys/SurveyNavigation"
import {fetcherGet, QUESTION_TYPE, QUESTION_TYPE_IDS} from "../../utilities/general"
import RefContext from "../../utilities/RefContext"
import {useStableNavigate} from "../../utilities/StableNavigateContext"

const ALWAYS_ENABLED_QUESTION_TYPE_ID = 10

const OrientationStatusReport = ({responseId: responseIdProp, onExit}) => {
    useEffect(() => {
        document.title = "Orientation Status Report"
    }, [])
    const {dispatch} = useContext(RefContext)
    const [submitting, setSubmitting] = useState(false)
    const {responseId: responseIdParam} = useParams()
    const responseId = responseIdProp ?? responseIdParam
    const [reportData, setReportData] = useState({
        orientee_id: null,
    })
    const [orienteeName, setOrienteeName] = useState("")
    const [questions, setQuestions] = useState([])
    const [currentIndex, setCurrentIndex] = useState(0)
    const [slideDirection, setSlideDirection] = useState("question-transition-next")
    const [emptyError, setEmptyError] = useState(false)
    const [complete, setComplete] = useState(false)

    const {data: questionTypesData, isLoading: qtLoading} = useSWR("/questions/question_types", fetcherGet, {
        fallbackData: [],
    })
    const {data: questionCategories, isLoading: qcLoading} = useSWR("/questions/question_categories", fetcherGet, {
        fallbackData: [],
    })
    const {data: response, isLoading: qLoading} = useSWR(`/questions/response/${responseId}`, fetcherGet)

    const loading = qtLoading || qcLoading || qLoading

    useEffect(() => {
        if (questions && questions.length > 0 && questions[currentIndex] && questions[currentIndex]?.orientee_name) {
            setOrienteeName(questions[currentIndex].orientee_name)
        }
    }, [currentIndex])

    useEffect(() => {
        if (response?.questions) {
            setQuestions(response.questions)

            const answers = {}

            const firstUnansweredIndex = response.questions.findIndex((q) => {
                const ra = q.response_answer
                const isCategory = q.question_type_id === QUESTION_TYPE_IDS.TOPIC_INTRO
                return !isCategory && (!ra || Object.keys(ra).length === 0)
            })

            if (firstUnansweredIndex === 1) {
                setCurrentIndex(0)
                setOrienteeName(response?.questions[0]?.orientee_name)
            } else {
                setCurrentIndex(firstUnansweredIndex === -1 ? 0 : firstUnansweredIndex)
                setOrienteeName(response?.questions[firstUnansweredIndex === -1 ? 0 : firstUnansweredIndex].orientee_name)
            }

            const typeNameById = Object.fromEntries((questionTypesData ?? []).map((qt) => [qt.id, qt.name]))
            response.questions.forEach((q) => {
                if (typeNameById[q.question_type_id] === QUESTION_TYPE.MULTI_SELECT_WITH_TEXT) {
                    const rows = q.response_answers ?? []
                    answers[q.question_id] = rows
                        .map((r) => r.answer_id)
                        .filter((id) => id != null)
                        .map(String)
                    answers[`${q.question_id}_other`] = rows.find((r) => r.answer_text)?.answer_text ?? ""
                    return
                }
                let answerId = q.response_answer?.answer_id
                if (typeof answerId === "number") {
                    answerId = String(answerId)
                }
                const answerText = q.response_answer?.answer_text

                answers[q.question_id] = answerId != null ? answerId : answerText
            })

            setReportData((prev) => ({
                ...prev,
                ...answers,
            }))
        }
    }, [response, questionTypesData])

    const navigate = useStableNavigate()

    const handleSaveClose = () => {
        if (onExit) {
            onExit()
        } else {
            navigate("/surveys/orientation-status-report-selection?tab=saved")
        }
    }

    if (loading || questions.length === 0) {
        return null
    }

    const currentQuestion = questions[currentIndex]
    const isLastQuestion = currentIndex === questions.length - 1
    const questionTypeNameById = Object.fromEntries(questionTypesData.map((qt) => [qt.id, qt.name]))
    const currentValue = currentQuestion ? reportData[currentQuestion.question_id] : undefined
    const otherValue = currentQuestion ? reportData[`${currentQuestion.question_id}_other`] : undefined

    const nextEnabled = () => {
        if (currentQuestion.question_type_id === ALWAYS_ENABLED_QUESTION_TYPE_ID) return true
        if (!currentQuestion) return false
        if (!currentQuestion.is_required) return true
        return currentValue != null && (!Array.isArray(currentValue) || currentValue.length > 0)
    }

    const saveCurrentAnswer = async () => {
        if (currentQuestion.question_type_id === ALWAYS_ENABLED_QUESTION_TYPE_ID) return

        const typeName = questionTypeNameById[currentQuestion.question_type_id]
        const payload = {question_id: currentQuestion.question_id}
        if (typeName === "text" || typeName === "number" || typeName === "date") {
            payload.answer_text = currentValue
        } else if (typeName === QUESTION_TYPE.MULTI_SELECT_WITH_TEXT) {
            payload.selections = Array.isArray(currentValue) ? currentValue : []
            payload.other = reportData[`${currentQuestion.question_id}_other`] ?? ""
        } else if (typeName === "select") {
            payload.answer_text = otherValue
            payload.answer_id = currentValue
        } else if (
            typeName === "" ||
            typeName === "rangeWithNotApplicable" ||
            typeName === "range"
        ) {
            payload.answer_id = currentValue
        }

        await wrappedPatch(`/surveys/answer/osr/${responseId}`, payload)
    }

    const next = async () => {
        if (!nextEnabled()) {
            setEmptyError(true)
            return
        }
        setEmptyError(false)
        setSlideDirection("question-transition-next")
        // Advance first, then save in the background so the next question renders immediately.
        setCurrentIndex((i) => Math.min(i + 1, questions.length - 1))
        try {
            await saveCurrentAnswer()
        } catch {
            dispatch("showToast", {
                show: true,
                variant: "danger",
                text: "An error occurred saving your answer. Please try again.",
            })
        }
    }

    const previous = () => {
        setSlideDirection("question-transition-prev")
        setEmptyError(false)
        setCurrentIndex((i) => Math.max(i - 1, 0))
    }

    const submit = async () => {
        if (!nextEnabled()) {
            setEmptyError(true)
            return
        }
        setEmptyError(false)
        setSubmitting(true)
        try {
            await saveCurrentAnswer()
            await wrappedPatch(`/surveys/${responseId}/complete`, {})
            setComplete(true)
        } catch (error) {
            dispatch("showToast", {
                show: true,
                variant: "danger",
                text: "An error occurred completeing the survey. Please try again.",
            })
        } finally {
            setSubmitting(false)
        }
    }

    const progressQuestions = questions.filter((q) => q.question_type_id !== QUESTION_TYPE_IDS.TOPIC_INTRO)

    const progressCurrent = progressQuestions.filter((q, idx) => {
        const originalIndex = questions.findIndex((original) => original.question_id === q.question_id)
        return originalIndex <= currentIndex
    }).length

    const progressTotal = progressQuestions.length

    return (
        <>
            <OSRHeader
                orienteeName={orienteeName}
                shiftDate={reportData["shift_date"] ? new Date(reportData["shift_date"]) : null}
                onSaveClose={!loading ? handleSaveClose : undefined}
            />
            <div className="container mw-800px px-md-0">
                {complete ? (
                    <SurveyComplete />
                ) : (
                    !loading && (
                        <div className="flex-column flex-row-fluid py-10">
                            <div className="stepper stepper-pills stepper-column d-flex flex-column flex-xl-row flex-row-fluid gap-5">
                                <div className="d-flex flex-row-fluid flex-center pt-0 card-px-0">
                                    <form className="pt-0 w-100 px-0">
                                        <>
                                            <SurveyProgressBar current={progressCurrent} total={progressTotal} />
                                            <div className="mb-20" key={currentIndex}>
                                                <div className={slideDirection}>
                                                    {currentQuestion && (
                                                        <QuestionRenderer
                                                            surveyType="osr"
                                                            question={currentQuestion}
                                                            questionCategories={questionCategories ?? []}
                                                            questionTypeNameById={questionTypeNameById}
                                                            value={currentValue}
                                                            emptyError={emptyError}
                                                            onChange={(val) => {
                                                                setReportData((prev) => ({
                                                                    ...prev,
                                                                    [currentQuestion.question_id]: val,
                                                                }))
                                                            }}
                                                            otherValue={otherValue}
                                                            onOtherChange={(txt) => {
                                                                setReportData((prev) => ({
                                                                    ...prev,
                                                                    [`${currentQuestion.question_id}_other`]: txt,
                                                                }))
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                            <SurveyNavigation
                                                next={next}
                                                nextEnabled={nextEnabled()}
                                                previous={previous}
                                                isFirstStep={currentIndex === 0}
                                                isLastQuestion={isLastQuestion}
                                                submit={submit}
                                                submitting={submitting}
                                            />
                                        </>
                                    </form>
                                </div>
                            </div>
                        </div>
                    )
                )}
            </div>
        </>
    )
}

export default OrientationStatusReport
